import type {
  AgentCapabilities,
  AgentConfiguration,
  AgentEditorData,
  AgentKnowledgeBases,
  AgentValidation,
  AgentVersion,
  EditableAgentConfiguration,
  KnowledgeBase,
  KnowledgeBaseSelection,
  KnowledgeSearchResult,
  SheetOutputProvisioning,
} from '@models/agent-configuration.model';

import type { AgentConfigDto } from './dtos/agent.dto';
import type { AgentCallActivity, AgentResponse } from './types/agent.type';

export type ActivateVersionParams = {
  id: string;
  versionId: string;
};

export type TestKnowledgeSearchParams = {
  id: string;
  question: string;
};

export type ReplaceConfigurationParams = {
  configuration: EditableAgentConfiguration;
  expectedRevision: number;
  id: string;
};

export type ReplaceKnowledgeBasesParams = {
  expectedRevision: number;
  id: string;
  knowledgeBases: Array<KnowledgeBaseSelection>;
};

export type PublishAgentParams = {
  expectedRevision: number;
  id: string;
  publishRequestId: string;
};

export type ProvisionSheetOutputParams = {
  expectedRevision: number;
  id: string;
  provisionRequestId: string;
};

export type ProvisionSheetOutputResult = {
  agentId: string;
  draftRevision: number;
  provisioning: SheetOutputProvisioning;
};

export abstract class AgentsRepository {
  public abstract activateVersion(params: ActivateVersionParams): Promise<void>;

  public abstract create(payload: AgentConfigDto): Promise<AgentResponse>;

  public abstract getById(id: string): Promise<AgentResponse>;

  public abstract getCapabilities(): Promise<AgentCapabilities>;

  public abstract getConfiguration(id: string): Promise<AgentConfiguration>;

  public abstract getEditorData(id: string): Promise<AgentEditorData>;

  public abstract getKnowledgeBases(): Promise<Array<KnowledgeBase>>;

  public abstract getKnowledgeSelection(
    id: string,
  ): Promise<AgentKnowledgeBases>;

  public abstract list(): Promise<Array<AgentResponse>>;

  public abstract listCalls(id: string): Promise<Array<AgentCallActivity>>;

  public abstract listVersions(id: string): Promise<Array<AgentVersion>>;

  public abstract provisionSheetOutput(
    params: ProvisionSheetOutputParams,
  ): Promise<ProvisionSheetOutputResult>;

  public abstract publish(params: PublishAgentParams): Promise<AgentVersion>;

  public abstract remove(id: string): Promise<void>;

  public abstract replaceConfiguration(
    params: ReplaceConfigurationParams,
  ): Promise<AgentConfiguration>;

  public abstract replaceKnowledgeBases(
    params: ReplaceKnowledgeBasesParams,
  ): Promise<AgentKnowledgeBases>;

  public abstract testKnowledgeSearch(
    params: TestKnowledgeSearchParams,
  ): Promise<KnowledgeSearchResult>;

  public abstract validate(id: string): Promise<AgentValidation>;
}
